/**
 * Smart-create resolver — "Créer avec l'IA…".
 *
 * Maps a free-text goal ("créer une simulation Monte Carlo de …") to a target
 * CrudPage create route, then the `useSmartCreate` hook either:
 *   - opens the CURRENT page's create dialog pre-filled (when the user is
 *     already on a CrudPage), or
 *   - queues a one-shot `pendingCreate` + navigates to the resolved route, and
 *     the target CrudPage opens its create dialog + AI-fills it on mount.
 *
 * Targets are the config-driven CrudPage routes (they expose a create surface
 * the copilot layer can drive); each entry lists FR/EN keywords. Resolution
 * picks the longest matching keyword so specific phrases win over generic ones.
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { copilotApi } from '@/services/copilot.service'
import { useCopilotAgentStore } from '@/stores/copilot-agent.store'

export interface SmartCreateTarget {
  /** First route segment (module slug). */
  module: string
  /** Full path to a CrudPage-backed create route. */
  route: string
  /** Resolved entity type / label hint. */
  entityType: string
  /** FR label for toasts. */
  label: string
}

interface TargetDef {
  route: string
  entityType: string
  label: string
  keywords: string[]
}

/**
 * CrudPage-backed create targets. All of these render <CrudPage> and therefore
 * register a create surface the copilot layer can open + pre-fill.
 */
const TARGETS: TargetDef[] = [
  // MAIN domain registers — custom pages that now expose a copilot create
  // surface (see useCreateSurface). Listed first so "créer un risque /
  // obligation / politique" navigates to the right register from any page.
  { route: '/risk-pro/register', entityType: 'risk', label: 'Risque', keywords: ['risque', 'risk', 'menace'] },
  { route: '/compliance-pro/register', entityType: 'obligation', label: 'Obligation', keywords: ['obligation', 'exigence', 'obligation réglementaire', 'obligation reglementaire', 'exigence réglementaire', 'exigence reglementaire'] },
  { route: '/governance-pro/policies', entityType: 'policy', label: 'Politique', keywords: ['politique', 'policy', 'directive', 'normative'] },
  // dashboard-pro
  { route: '/dashboard-pro/monte-carlo', entityType: 'simulation', label: 'Simulation Monte Carlo', keywords: ['monte carlo', 'monte-carlo', 'simulation'] },
  { route: '/dashboard-pro/what-if-builder', entityType: 'scenario', label: 'Scénario What-If', keywords: ['what-if', 'what if', 'scénario', 'scenario'] },
  { route: '/dashboard-pro/cohort-analysis', entityType: 'cohort', label: 'Analyse de cohorte', keywords: ['cohorte', 'cohort'] },
  { route: '/dashboard-pro/bayesian-network', entityType: 'bayesian_node', label: 'Réseau bayésien', keywords: ['bayes', 'bayésien', 'bayesian'] },
  // strategy-pro
  { route: '/strategy-pro/resource-optimizer', entityType: 'resource_allocation', label: 'Allocation de ressources', keywords: ['ressource', 'resource', 'allocation', 'optimiseur'] },
  { route: '/strategy-pro/deal-room', entityType: 'deal', label: 'Opportunité (deal)', keywords: ['deal', 'opportunité', 'opportunite'] },
  { route: '/strategy-pro/client-nps-csat', entityType: 'satisfaction', label: 'Enquête NPS / CSAT', keywords: ['nps', 'csat', 'satisfaction client'] },
  { route: '/strategy-pro/partner-ecosystem', entityType: 'partner', label: 'Partenaire (écosystème)', keywords: ['partenaire', 'partner', 'écosystème', 'ecosysteme'] },
  { route: '/strategy-pro/deliverable-quality-scorer', entityType: 'deliverable', label: 'Qualité de livrable', keywords: ['livrable', 'deliverable', 'qualité livrable'] },
  { route: '/strategy-pro/client-expectation-tracker', entityType: 'expectation', label: 'Attente client', keywords: ['attente client', 'expectation'] },
  { route: '/strategy-pro/scope-creep-detector', entityType: 'scope_change', label: 'Dérive de périmètre', keywords: ['scope creep', 'dérive', 'perimetre', 'périmètre'] },
  { route: '/strategy-pro/sprint-retrospective', entityType: 'retrospective', label: 'Rétrospective de sprint', keywords: ['rétrospective', 'retrospective', 'sprint'] },
  // ai-ia-pro
  { route: '/ai-ia-pro/data-product-catalog', entityType: 'data_product', label: 'Data product', keywords: ['data product', 'produit de données', 'produit de donnees', 'catalogue data'] },
  { route: '/ai-ia-pro/cost-per-query', entityType: 'cost_metric', label: 'Coût par requête', keywords: ['coût par requête', 'cout par requete', 'cost per query'] },
  { route: '/ai-ia-pro/dataops-pipeline-monitor', entityType: 'pipeline', label: 'Pipeline DataOps', keywords: ['dataops', 'pipeline'] },
  { route: '/ai-ia-pro/schema-evolution-tracker', entityType: 'schema_change', label: 'Évolution de schéma', keywords: ['schema evolution', 'évolution de schéma', 'evolution de schema', 'schéma'] },
  // change-pro
  { route: '/change-pro/certification-pathway', entityType: 'certification_path', label: 'Parcours de certification', keywords: ['certification pathway', 'parcours certification', 'parcours de certification'] },
  { route: '/change-pro/competency-matrix', entityType: 'competency', label: 'Matrice de compétences', keywords: ['compétence', 'competence', 'competency', 'matrice compétence'] },
  { route: '/change-pro/learning-analytics', entityType: 'learning_metric', label: 'Analytique de formation', keywords: ['learning analytics', 'analytique formation', 'analytique de formation'] },
  { route: '/change-pro/lms-integration', entityType: 'lms', label: 'Intégration LMS', keywords: ['lms', 'intégration lms'] },
  // governance-pro
  { route: '/governance-pro/cross-reference-checker', entityType: 'cross_reference', label: 'Référence croisée', keywords: ['cross reference', 'cross-reference', 'référence croisée', 'reference croisee'] },
  { route: '/governance-pro/interview-transcript-analyzer', entityType: 'interview', label: 'Entretien (transcription)', keywords: ['interview', 'entretien', 'transcription'] },
  { route: '/governance-pro/template-intelligence', entityType: 'template', label: 'Modèle intelligent', keywords: ['template intelligence', 'modèle intelligent', 'modele intelligent'] },
  { route: '/governance-pro/working-paper-reviewer', entityType: 'working_paper', label: 'Papier de travail', keywords: ['working paper', 'papier de travail'] },
  { route: '/governance-pro/regulatory-database-search', entityType: 'regulation', label: 'Recherche réglementaire', keywords: ['regulatory database', 'recherche réglementaire', 'recherche reglementaire'] },
  // security-pro
  { route: '/security-pro/incident-forensics', entityType: 'forensic', label: 'Investigation forensique', keywords: ['forensic', 'forensique', 'investigation incident'] },
  { route: '/security-pro/red-team-planner', entityType: 'red_team', label: 'Plan Red Team', keywords: ['red team', 'équipe rouge', 'equipe rouge'] },
  { route: '/security-pro/security-architecture-review', entityType: 'architecture_review', label: 'Revue d’architecture sécurité', keywords: ['architecture sécurité', 'architecture securite', 'security architecture'] },
  { route: '/security-pro/supply-chain-cyber-risk', entityType: 'supply_chain_risk', label: 'Risque cyber chaîne d’appro.', keywords: ['supply chain', 'chaîne d’approvisionnement', 'chaine appro', 'cyber fournisseur'] },
]

/** Resolve a create target from free text, or null when nothing matches. */
export function resolveCreateTarget(text: string): SmartCreateTarget | null {
  const t = (text || '').toLowerCase()
  let best: TargetDef | null = null
  let bestScore = 0
  for (const def of TARGETS) {
    for (const kw of def.keywords) {
      if (kw.length > bestScore && t.includes(kw)) {
        best = def
        bestScore = kw.length
      }
    }
  }
  if (!best) return null
  const module = best.route.split('/').filter(Boolean)[0] ?? ''
  return { module, route: best.route, entityType: best.entityType, label: best.label }
}

/**
 * Hook: run smart-create for a free-text goal.
 *
 * Optionally pass `onBeforeNavigate` (e.g. to close the copilot sidebar) so the
 * target create dialog is visible instead of stacked behind the panel.
 */
export function useSmartCreate() {
  const navigate = useNavigate()
  const requestCreate = useCopilotAgentStore((s) => s.requestCreate)

  return useCallback(
    async (goal: string, onBeforeNavigate?: () => void) => {
      const text = (goal || '').trim()
      if (!text) return

      const target = resolveCreateTarget(text)
      const cur = useCopilotAgentStore.getState().surface

      // Prefer the CURRENT CrudPage when it is the resolved target OR nothing
      // specific was resolved — the user is most likely creating here.
      if (cur && (!target || target.module === cur.module)) {
        onBeforeNavigate?.()
        cur.open()
        try {
          const res = await copilotApi.fillForm({
            form_schema: cur.fields,
            prompt: text,
            module: cur.module,
          })
          cur.applyPrefill(res.fields)
          toast.success('Formulaire pré-rempli par l’IA — vérifiez puis validez')
        } catch {
          toast.error('Pré-remplissage IA indisponible — complétez manuellement')
        }
        return
      }

      if (target) {
        onBeforeNavigate?.()
        requestCreate({ module: target.module, prompt: text })
        navigate(target.route)
        toast.success(`Ouverture du formulaire : ${target.label}`)
        return
      }

      toast.error(
        'Précisez le type d’enregistrement (ex : « créer une simulation Monte Carlo »).',
      )
    },
    [navigate, requestCreate],
  )
}
